import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { searchForCount, lineLink, costsForOrgItems } from '../lib/orgCatalog';
import { ArrowLeft, Tick } from '../components/Glyph';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import OwnershipTransferModal from '../components/OwnershipTransferModal';
import { useAuth } from '../components/AuthContext';
import { useToast } from '../components/ToastContext';
import { StatusPill } from './countEntry/countEntryHelpers';
import { notify } from '../lib/notify';
import { countRules, bigChanges } from '../lib/countRules';
import * as outbox from '../lib/outbox';
import * as sheetWatch from '../lib/sheetWatch';
import { ago } from '../lib/when';
import StopCard from '../components/StopCard';
import SystemShell from '../components/SystemShell';
import ConnectionBar from '../components/ConnectionBar';
import { logAudit } from '../hooks/useAudit';
import AppHeader from '../components/AppHeader';
import { DeskFrame } from '../components/AppFrame';
import PageBar from '../components/PageBar';
import LockedView from './countEntry/LockedView';
import CountBar from './countEntry/CountBar';
import NoteModal from './countEntry/modals/NoteModal';
import EditItemModal from './countEntry/modals/EditItemModal';
import RemoveModal from './countEntry/modals/RemoveModal';
import SubmitModal from './countEntry/modals/SubmitModal';
import { keyAction, nextIndex, refusesKey, SEARCH, NOTE, UNDO } from './countEntry/keys';
import SplitView from './countEntry/SplitView';
import QuickEntry from './countEntry/QuickEntry';
import { otherCounters, sharedSentence, mergeLive } from './countEntry/shared';
import { fetchAll, save, saveChanged } from '../lib/save';
import { sendSheetIn } from './countEntry/submitSheet';
import { readPreference, writePreference, ITEM_LOCATIONS } from '../lib/preferences';
import ClosureModal from './countEntry/modals/ClosureModal';
import ScanModal from './countEntry/modals/ScanModal';
import CustomItemModal from './countEntry/modals/CustomItemModal';
import DupeLocationModal from './countEntry/modals/DupeLocationModal';
import LineList from './countEntry/LineList';
import { SheetProgress, SharedBand, sheetProgress, isUncounted } from './countEntry/lineState';
import PageLoading from '../components/PageLoading';
import { trace } from '../lib/trace';
import { pressable } from '../lib/pressable';

/*
  A refused audit write says so, in the console.

  **It must not block the person counting**, which is why this does not stop
  anything and does not raise a message on screen: somebody on a shelf with a
  clipboard can do nothing about the record of their own change. But throwing
  the database's answer away entirely is how this went wrong for months. Four of
  the seven kinds of change the app records were refused every single time by a
  rule on the table that allowed only three words, and the history table held
  zero rows, ever. Nothing said so anywhere.
*/
/*
  The orders a sheet can be read in, offered on the phone's Order sheet and
  pressed as headings on a desk.

  **There were two orders and they disagreed.** A desk sorted by a column with a
  direction, and a phone had five of its own that included two the desk did not
  offer, so the same person saw their lines in a different order depending on
  what they were holding. Worse, the phone's ignored the grouping that keeps a
  line's second and third shelf directly under the first, so an item counted in
  two places appeared twice, far apart, with nothing to connect them.
*/
export const SORT_CHOICES = [
  { col: 'item_number_raw', dir: 'asc',  label: 'Item number, A to Z' },
  { col: 'item_number_raw', dir: 'desc', label: 'Item number, Z to A' },
  { col: 'description_raw', dir: 'asc',  label: 'Description, A to Z' },
  { col: 'vendor_raw',      dir: 'asc',  label: 'Vendor, A to Z' },
  { col: 'location',        dir: 'asc',  label: 'Where it sits, A to Z' },
];

// What the current order is called, for the line under the search box.
export function sortLabel(col, dir) {
  const hit = SORT_CHOICES.find(o => o.col === col && o.dir === dir);
  return hit ? hit.label : '';
}

function warnHistory(err) {
  // eslint-disable-next-line no-console
  console.warn('The line history was not written: ' + (err?.message || err));
}


export default function CountEntry() {
  const { countId }  = useParams();
  const { profile }  = useAuth();
  const navigate     = useNavigate();
  const toast        = useToast();

  const [count, setCount]             = useState(null);
  // The check pop (rebrand step 3, guide 10.5): which line just saved. Shown
  // for 1.4 seconds after the database takes a number, and never otherwise.
  const [savedId, setSavedId]         = useState(null);
  const savedTimer                    = useRef(null);
  function flashSaved(itemId) {
    clearTimeout(savedTimer.current);
    setSavedId(itemId);
    savedTimer.current = setTimeout(() => setSavedId(null), 1400);
  }
  useEffect(() => () => clearTimeout(savedTimer.current), []);
  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [submitting, setSubmitting]   = useState(false);

  // Search state
  const [descSearch, setDescSearch]             = useState('');
  const [catalogResults, setCatalogResults]     = useState([]);
  const [searching, setSearching]               = useState(false);
  const searchTimer = useRef(null);

  // Modal state
  const [showSubmitModal, setShowSubmitModal]   = useState(false);
  const [showClosureModal, setShowClosureModal] = useState(false);
  const [showScanModal, setShowScanModal]       = useState(false);
  const [customModalValue, setCustomModalValue] = useState('');
  const [showCustomModal, setShowCustomModal]   = useState(false);
  const [scanTriggeredCustomModal, setScanTriggeredCustomModal] = useState(false);

  // Duplicate / multi-location modal state
  const [dupeModal, setDupeModal] = useState(null);
  // dupeModal = { existingItem, pendingCatalogItem, pendingCustom, countedByName }
  const dupeModalRef = useRef(null); // ref copy so callbacks never have stale closure

  // Session lock state (live concurrent session lock, 30 min)
  const [sessionBlocked, setSessionBlocked] = useState(false);
  const [blockedByName, setBlockedByName]   = useState('');
  const [blockedSince, setBlockedSince]     = useState(null);
  const [watchingSheet, setWatchingSheet]   = useState(false);
  const sessionLockRef = useRef(false);

  // Rep ownership lock state (permanent claim: whoever started this count owns it until submit)
  const [ownershipLocked, setOwnershipLocked] = useState(false); // true = someone else claimed it
  const [ownerName, setOwnerName]             = useState('');
  const [adminOverriding, setAdminOverriding] = useState(false); // admin editing a count claimed by another user
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Warehouse real-time state
  const [isWarehouse, setIsWarehouse]       = useState(false);
  const realtimeChannelRef                  = useRef(null);

  // Responsive state
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [actionMenu, setActionMenu]   = useState(null); // item.id with open menu

  // Mobile: measure the floating header card so the scroll body clears it
  // (content slides under the glass). ResizeObserver covers content-driven
  // height changes; windowWidth re-attaches when the compact tree mounts.
  const mobHeaderRef = useRef(null);
  const [mobHeaderH, setMobHeaderH] = useState(210);
  /*
    **Measured before the paint, not after it.** The header below 1024 floats
    over the list, so the list is padded down by however tall the header is. That
    was measured in an ordinary effect, which runs AFTER the browser has drawn,
    so the first frame of every load put the search box underneath the header.

    Found on 5 September 2026 by photographing count entry at 390 for the first
    time: adding one line to the header made the gap wrong by exactly that line,
    and the picture caught it because a photograph IS the first frame. A person
    scrolling would have seen it correct itself and thought nothing of it, which
    is why it had survived.

    `useLayoutEffect` measures before the browser paints, and it re-measures when
    anything that changes the header's height changes.
  */
  useLayoutEffect(() => {
    const el = mobHeaderRef.current;
    if (!el) return;
    const measure = () => setMobHeaderH(el.offsetHeight);
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { if (ro) ro.disconnect(); };
    // `locationEnabled` is declared further down this component, so it cannot be
    // named here: reading a const before its declaration throws, and the page
    // then comes back a completely blank grey rectangle with nothing to say why.
    // It happened on the first attempt at this line. `items.length` covers the
    // list growing and the observer covers everything else.
  }, [windowWidth, items.length]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close action menu when clicking anywhere outside
  useEffect(() => {
    if (!actionMenu) return;
    const handleClickOutside = () => setActionMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [actionMenu]);

  // Rebrand step 5 (guide 12.1): a tablet is 600 to 1023, a phone under 600.
  const isTablet = windowWidth >= 600 && windowWidth < 1024;
  const isMobile = windowWidth < 600;
  const isCompact = isTablet || isMobile;

  // Location feature state
  /*
    **Item Locations is remembered against the person now.** It was held here
    and written nowhere, so it started off on every open and the reps who do
    record where a box sits had to switch it on again for every location, every
    quarter. Off is still the right default for somebody who has never touched
    it: the column is empty until a company has been round once.
  */
  const [locationEnabled, setLocationEnabled]   = useState(false);

  useEffect(() => {
    let live = true;
    readPreference(profile?.id, ITEM_LOCATIONS, 'off').then(v => {
      if (live) setLocationEnabled(v === 'on');
    });
    return () => { live = false; };
  }, [profile?.id]);

  async function toggleItemLocations() {
    const next = !locationEnabled;
    setLocationEnabled(next);
    const ok = await writePreference(profile?.id, ITEM_LOCATIONS, next ? 'on' : 'off');
    // Put the switch back rather than leaving it somewhere the database does
    // not agree with. A setting that looks saved and is not is worse than one
    // that plainly did not save.
    if (!ok) {
      setLocationEnabled(!next);
      toast.error('That switch could not be saved, so it has been put back.');
    }
  }
  const [editingLocation, setEditingLocation]   = useState(null); // count line row id being edited
  const [locationDraft, setLocationDraft]       = useState('');

  // Sort state
  const [sortCol, setSortCol]   = useState('item_number_raw');
  const [sortDir, setSortDir]   = useState('asc');
  const [showSortSheet, setShowSortSheet]       = useState(false);
  const [noteModal, setNoteModal]               = useState(null);
  const [editModal, setEditModal]               = useState(null);
  const [removeModal, setRemoveModal]           = useState(null);

  /*
    **The keyboard, for somebody typing a whole sheet in from paper.** The
    approved desktop drawing carries the four keys along the bottom of the list.
    The rule itself is `src/pages/countEntry/keys.js`, where it can be tested;
    these two are what it needs to act on: every quantity box on the screen, by
    line, and the search box to jump to.
  */
  const qtyRefs   = useRef({});
  const searchRef = useRef(null);
  // Which line the cursor is in, if any. The live feed reads it so that another
  // person saving the same line cannot pull the number out from under it.
  const focusedLine = useRef(null);

  function focusQty(id) {
    /*
      **A line has two quantity boxes**, one in the wide shape and one in the
      narrow block, and the stylesheet shows whichever fits. `offsetParent` is
      empty for anything not being drawn, so this takes the one on screen.
      Without it the keyboard focused the invisible copy and looked, to a
      person pressing Enter, exactly like the key doing nothing.
    */
    const el = [qtyRefs.current[id + ':wide'], qtyRefs.current[id + ':narrow'], qtyRefs.current[id + ':detail']]
      .find(x => x && x.offsetParent !== null);
    if (!el) return;
    el.focus();
    // Selected rather than merely focused, so the next number typed replaces
    // what is there. Somebody typing a sheet in is entering a count, not
    // editing one, and having to clear the box first is the whole saving gone.
    if (typeof el.select === 'function') el.select();
  }

  /*
    **Rebrand step 5 (guide 12.3)** adds four things to the keys in a count box:
    Down and Up move like Enter and Shift and Enter; Escape puts back the number
    the box had when the cursor went in, through the same save; a letter is
    refused with a jostle and the cursor stays; and on a computer / goes to the
    quick entry bar. On a tablet, moving means choosing the next line, whose box
    then takes the cursor.
  */
  const [activeId, setActiveId]   = useState(null);   // the line whose box has the cursor
  const [flashId, setFlashId]     = useState(null);   // a row quick entry just saved
  const [invalidId, setInvalidId] = useState(null);   // a box that just refused a key
  const [selectedId, setSelectedId] = useState(null); // the tablet's chosen line
  const [lineFilter, setLineFilter] = useState('all');
  const focusedValue = useRef({});
  const quickRef = useRef(null);
  const invalidTimer = useRef(null);
  const flashTimer = useRef(null);
  useEffect(() => () => { clearTimeout(invalidTimer.current); clearTimeout(flashTimer.current); }, []);

  // "/" anywhere on a computer, outside a field and with no dialog open, goes
  // to the quick entry bar (guide 12.3).
  useEffect(() => {
    if (isCompact) return undefined;
    const onKey = e => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      if (document.querySelector('.dlg-overlay, .modal-overlay')) return;
      if (!quickRef.current) return;
      e.preventDefault();
      quickRef.current.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isCompact]);

  function refuseKey(id) {
    setInvalidId(null);
    clearTimeout(invalidTimer.current);
    requestAnimationFrame(() => setInvalidId(id));
    invalidTimer.current = setTimeout(() => setInvalidId(null), 1200);
  }

  function onQtyKey(e, list, index, item) {
    const action = keyAction(e);
    if (!action) {
      if (refusesKey(e)) { e.preventDefault(); refuseKey(item.id); }
      return;
    }
    e.preventDefault();
    if (action === SEARCH) {
      const to = !isCompact && quickRef.current ? quickRef.current : searchRef.current;
      to?.focus();
      if (to === searchRef.current && typeof to?.select === 'function') to.select();
      return;
    }
    if (action === NOTE) { setNoteModal(item); return; }
    if (action === UNDO) {
      const before = focusedValue.current[item.id];
      if (before !== undefined) updateQty(item.id, before === null ? '' : before);
      return;
    }
    const to = nextIndex(index, action, list.length);
    if (to < 0) return;
    if (isTablet) { chooseLine(list[to].id, true); return; }
    focusQty(list[to].id);
  }

  // The tablet's chosen line, and its box takes the cursor when asked.
  function chooseLine(id, focus) {
    setSelectedId(id);
    if (focus) requestAnimationFrame(() => focusQty(id));
  }

  const [closureForm, setClosureForm] = useState({
    reason: '', notes: '', last_count_date: '', final_count_performed: '', inventory_retrieved: '',
  });

  // Keep dupeModalRef in sync
  useEffect(() => { dupeModalRef.current = dupeModal; }, [dupeModal]);


  useEffect(() => { loadCount(); }, [countId]); // eslint-disable-line

  // Release session lock and real-time channel on unmount
  useEffect(() => {
    const handleUnload = () => releaseSessionLock();
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      releaseSessionLock();
      if (realtimeChannelRef.current) supabase.removeChannel(realtimeChannelRef.current);
    };
  }, [countId]); // eslint-disable-line

  // Refresh the session lock every 10 minutes
  useEffect(() => {
    if (!profile?.id) return;
    const interval = setInterval(async () => {
      if (sessionLockRef.current) {
        await supabase
          .from('inventory_counts')
          .update({ session_started_at: new Date().toISOString() })
          .eq('id', countId)
          .eq('session_user_id', profile.id);
      }
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [countId, profile?.id]); // eslint-disable-line

  // Search triggered by desc field
  useEffect(() => {
    if (descSearch.length >= 2) {
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => searchCatalog(descSearch), 200);
    } else {
      setCatalogResults([]);
    }
    return () => clearTimeout(searchTimer.current);
  }, [descSearch]);

  async function acquireSessionLock(countData) {
    if (!countData || !profile?.id) return true;
    if (countData.status === 'submitted' || countData.status === 'approved') return true;
    // A location several people count at once skips the lock on purpose
    if (countData.warehouse?.allows_concurrent_counting) return true;
    const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
    if (
      countData.session_user_id &&
      countData.session_user_id !== profile.id &&
      countData.session_started_at > thirtyMinAgo
    ) {
      const { data: blocker } = await supabase
        .from('profiles').select('full_name').eq('id', countData.session_user_id).single();
      // **"Somebody else", not "another user".** The name starts a sentence on
      // the card now, and "another user has this count open" reads as a typo.
      // It is also not a phrase anybody says out loud about a colleague.
      setBlockedByName(blocker?.full_name || 'Somebody else');
      // When they last touched it, so the card can say how long it has been
      // held. Group H's drawing puts that beside how long until it frees,
      // because together they answer "should I wait or move on".
      setBlockedSince(countData.session_started_at);
      setSessionBlocked(true);
      return false;
    }
    await supabase
      .from('inventory_counts')
      .update({ session_user_id: profile.id, session_started_at: new Date().toISOString() })
      .eq('id', countData.id);
    sessionLockRef.current = true;
    return true;
  }

  async function releaseSessionLock() {
    if (!sessionLockRef.current || !countId) return;
    sessionLockRef.current = false;
    await supabase
      .from('inventory_counts')
      .update({ session_user_id: null, session_started_at: null })
      .eq('id', countId);
  }

  function setupRealtimeSync(countId) {
    // Clean up any existing channel
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
    }
    const channel = supabase
      .channel('warehouse-count-' + countId)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'count_line_items',
        filter: 'inventory_count_id=eq.' + countId,
      }, payload => {
        const newItem = payload.new;
        setItems(prev => {
          // Ignore if we already have this item (our own insert)
          if (prev.find(i => i.id === newItem.id)) return prev;
          return [...prev, newItem].sort((a, b) => {
            const cmp = (a.item_number_raw || '').localeCompare(b.item_number_raw || '');
            if (cmp !== 0) return cmp;
            return (a.location_seq || 1) - (b.location_seq || 1);
          });
        });
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'count_line_items',
        filter: 'inventory_count_id=eq.' + countId,
      }, payload => {
        // **A line somebody is typing into is left alone**, which is what the
        // band at the top of a shared location promises. The rule is
        // `mergeLive` in countEntry/shared.js, where it is tested.
        setItems(prev => mergeLive(prev, payload.new, focusedLine.current));
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'count_line_items',
        filter: 'inventory_count_id=eq.' + countId,
      }, payload => {
        setItems(prev => prev.filter(i => i.id !== payload.old.id));
      })
      .subscribe();
    realtimeChannelRef.current = channel;
  }

  async function loadCount() {
    setLoading(true);
    const { data: countData } = await supabase
      .from('inventory_counts')
      .select('id, status, submitted_at, rep_id, closure_reason, closure_notes, session_user_id, session_started_at, warehouse:warehouses(id, name, allows_concurrent_counting, region:regions(name)), cycle:count_cycles(name)')
      .eq('id', countId).single();

    const warehouse = !!countData?.warehouse?.allows_concurrent_counting;
    setIsWarehouse(warehouse);

    let finalCount = countData;

    // --- Ownership resolution at load time ---
    // If rep_id is already someone else (and not me, not warehouse, not admin) -> I'm locked out
    // If rep_id is null and I'm a rep/manager -> CLAIM IT NOW so no one else can
    // Admins never claim; they just view.
    const isRep = profile?.role === 'rep' || profile?.role === 'manager';

    if (
      countData?.rep_id &&
      profile?.id &&
      countData.rep_id !== profile.id &&
      !warehouse &&
      profile.role !== 'admin'
    ) {
      // Someone else owns it -- lock to read-only
      const { data: owner } = await supabase
        .from('profiles').select('full_name').eq('id', countData.rep_id).single();
      setOwnerName(owner?.full_name || 'another rep');
      setOwnershipLocked(true);
      setAdminOverriding(false);
    } else if (
      countData?.rep_id &&
      profile?.id &&
      countData.rep_id !== profile.id &&
      !warehouse &&
      profile.role === 'admin'
    ) {
      // Admin override -- fetch owner name for the banner, but do NOT lock
      const { data: owner } = await supabase
        .from('profiles').select('full_name').eq('id', countData.rep_id).single();
      setOwnerName(owner?.full_name || 'another rep');
      setOwnershipLocked(false);
      setAdminOverriding(true);
    } else if (
      !countData?.rep_id &&
      isRep &&
      profile?.id &&
      !warehouse &&
      (countData?.status === 'not_started' || countData?.status === 'in_progress')
    ) {
      // Claim ownership through claim_count, which tests that the sheet is
      // free and takes it in one database statement, so two reps opening the
      // same sheet cannot both end up believing they own it. It returns
      // whoever holds the sheet afterwards.
      const { data: nowOwnedBy, error: claimError } = await supabase
        .rpc('claim_count', { p_count_id: countId });

      if (claimError) {
        // Say so rather than opening a sheet that cannot be saved.
        toast.error('Could not start this count: ' + claimError.message);
        setOwnershipLocked(true);
      } else if (nowOwnedBy === profile.id) {
        // Claim succeeded -- I own this count now
        finalCount = { ...countData, rep_id: profile.id };
        setOwnershipLocked(false);
        setOwnerName('');
      } else {
        // Someone else claimed it in the split second between our read and write
        if (nowOwnedBy && nowOwnedBy !== profile.id) {
          const { data: owner } = await supabase
            .from('profiles').select('full_name').eq('id', nowOwnedBy).single();
          setOwnerName(owner?.full_name || 'another rep');
          setOwnershipLocked(true);
          finalCount = { ...countData, rep_id: nowOwnedBy };
        } else {
          setOwnershipLocked(false);
          setOwnerName('');
        }
      }
    } else {
      setOwnershipLocked(false);
      setOwnerName('');
    }

    setCount(finalCount);

    // Skip live session lock if ownership is held by another rep -- we're view-only,
    // and we don't want to block the actual owner.
    const isOwnershipLocked =
      finalCount?.rep_id &&
      profile?.id &&
      finalCount.rep_id !== profile.id &&
      !warehouse &&
      profile.role !== 'admin';

    if (!isOwnershipLocked) {
      const canProceed = await acquireSessionLock(finalCount);
      if (!canProceed) { setLoading(false); return; }
    }

    if (countData) {
      /*
        **Every line, a page at a time.** A request brings back about a thousand
        rows and says nothing about the rest, and this asked once. The biggest
        sheet in the database today is 300 lines so nothing is short right now,
        but the approved drawing itself shows a 486 line sheet and a regional
        office can run past a thousand. A count sheet that quietly stops is the
        worst version of that fault: a rep counts what is on the screen, hands it
        in believing it complete, and the lines that never arrived are simply
        never counted.
      */
      const { data: rawLines } = await fetchAll(() => supabase
        .from('count_line_items').select('*, item:item_catalog_id(cost_per_item)').eq('inventory_count_id', countId).order('item_number_raw'));
      /* A line from the new catalog (job 37, 11 October 2026) gets its cost
         each from there, in the same place the old table's cost was. */
      let lineItems = rawLines;
      const orgIds = (rawLines || []).filter(l => l.org_item_id).map(l => l.org_item_id);
      if (orgIds.length) {
        const { costs } = await costsForOrgItems(orgIds);
        lineItems = rawLines.map(l => (l.org_item_id ? { ...l, item: { cost_per_item: costs.get(l.org_item_id) ?? null } } : l));
      }

      // Load this location's remembered shelf positions and merge them into
      // rows that don't already have one set
      let mergedItems = lineItems || [];
      if (countData?.warehouse?.id) {
        const { data: locData } = await supabase
          .from('item_locations')
          .select('item_number, location')
          .eq('warehouse_id', countData.warehouse.id);
        if (locData?.length) {
          const locMap = {};
          for (const l of locData) locMap[l.item_number] = l.location;
          // Merge: if row has no location set, seed it from item_locations
          mergedItems = mergedItems.map(item => ({
            ...item,
            location: item.location || locMap[item.item_number_raw] || null,
          }));
        }
      }
      // Show the Location column if anything in this count has a position on it.
      // Previously this only looked at item_locations, so a count whose own lines
      // carried positions opened with the column hidden.
      if (mergedItems.some(i => i.location)) setLocationEnabled(true);
      setItems(mergedItems);
    }
    setLoading(false);

    // Live sync, for the locations several people count at once
    if (warehouse && (countData?.status === 'not_started' || countData?.status === 'in_progress')) {
      setupRealtimeSync(countId);
    }
  }

  async function searchCatalog(query) {
    if (!query || query.length < 2) { setCatalogResults([]); return; }
    setSearching(true);
    const q = query.toLowerCase();
    /*
      **Both catalogs, job 37, 11 October 2026.** A company's own items come
      from the new catalog, by any of their numbers or their description; the
      old table is still asked too, because OnHand Testing's items are only
      there. Each company only ever finds its own, so the two never mix.
    */
    const [fresh, { data }] = await Promise.all([
      searchForCount(query, 15),
      supabase
        .from('items')
        .select('id, item_number, description, vendor')
        .or(`item_number.ilike.%${q}%,description.ilike.%${q}%,vendor.ilike.%${q}%`)
        .limit(15),
    ]);
    // Don't filter out items already in count
    // so the duplicate/location modal can trigger when the user selects them
    setCatalogResults([...(fresh.items || []), ...(data || [])].slice(0, 15));
    setSearching(false);
  }

  // Auto-flip to in_progress on first interaction if still not_started.
  // Also claims rep_id ownership: whoever first enters data owns the count until submitted.
  async function ensureInProgress() {
    if (ownershipLocked) return; // safety: never let a locked-out viewer write

    // A sheet nobody holds has to be claimed before it can be written to, and
    // claiming goes through privileged database code rather than a plain save.
    let repId = count?.rep_id;
    if (!repId && profile?.id) {
      const { data: nowOwnedBy, error } = await supabase
        .rpc('claim_count', { p_count_id: countId });
      if (error) { toast.error('Could not start this count: ' + error.message); return; }
      if (nowOwnedBy !== profile.id) {
        // At a regional office somebody else starting it first is the point:
        // everybody who covers it counts it (the owner's word, 18 September
        // 2026). Anywhere else it is theirs now.
        if (isWarehouse && nowOwnedBy) {
          setCount(prev => ({ ...prev, rep_id: nowOwnedBy }));
          return;
        }
        toast.error('Somebody else has just started this count.');
        setOwnershipLocked(true);
        return;
      }
      repId = nowOwnedBy;
      setCount(prev => ({ ...prev, rep_id: repId }));
    }

    if (count?.status === 'not_started') {
      const { error } = await supabase
        .from('inventory_counts').update({ status: 'in_progress' }).eq('id', countId);
      if (error) { toast.error('Could not start this count: ' + error.message); return; }
      setCount(prev => ({ ...prev, status: 'in_progress' }));
    }
  }

  async function addCatalogItem(catalogItem, locationOverride) {
    const exists = items.find(i => i.item_number_raw?.toLowerCase() === catalogItem.item_number?.toLowerCase());
    if (exists && !locationOverride) {
      // Fetch who counted it
      const { data: counter } = await supabase.from('profiles').select('full_name').eq('id', exists.last_updated_by || exists.updated_by || '').maybeSingle();
      setDupeModal({ existingItem: exists, pendingCatalogItem: catalogItem, countedByName: counter?.full_name || exists.last_updated_name || 'another user' });
      setCatalogResults([]); setDescSearch('');
      return;
    }
    await ensureInProgress();
    const now = new Date().toISOString();
    const nextSeq = locationOverride
      ? items.filter(i => i.item_number_raw?.toLowerCase() === catalogItem.item_number?.toLowerCase()).length + 1
      : 1;
    const { data: newItem } = await supabase.from('count_line_items').insert({
      inventory_count_id: countId,
      ...lineLink(catalogItem),
      item_number_raw:    catalogItem.item_number,
      description_raw:    catalogItem.description,
      vendor_raw:         catalogItem.vendor,
      quantity: 0, is_new_item: true, not_in_catalog: false,
      location: locationOverride || null,
      location_seq: nextSeq,
      first_counted_at: now, last_updated_at: now,
      last_updated_name: profile?.full_name || '',
    }).select().single();
    if (newItem) {
      await supabase.from('count_line_item_history').insert({
        line_item_id:       newItem.id,
        inventory_count_id: countId,
        item_number:        catalogItem.item_number,
        description:        catalogItem.description,
        old_quantity:       null,
        new_quantity:       0,
        changed_by:         profile?.id || null,
        changed_by_name:    profile?.full_name || '',
        changed_at:         now,
        change_type:        'first_count',
      });
      setItems(prev => [...prev, newItem].sort((a, b) => {
        const cmp = a.item_number_raw?.localeCompare(b.item_number_raw);
        if (cmp !== 0) return cmp;
        return (a.location_seq || 1) - (b.location_seq || 1);
      }));
      setCatalogResults([]); setDescSearch('');
      setDupeModal(null);
      toast.success('Added ' + catalogItem.item_number + (locationOverride ? ' (' + locationOverride + ')' : ''));
    }
  }

  async function addCustomItem({ itemNumber, description, vendor, location, quantity }, locationOverride) {
    const itemNum = itemNumber.trim().toUpperCase();
    const exists = items.find(i => i.item_number_raw?.toUpperCase() === itemNum);
    if (exists && !locationOverride) {
      const { data: counter } = await supabase.from('profiles').select('full_name').eq('id', exists.last_updated_by || exists.updated_by || '').maybeSingle();
      setDupeModal({
        existingItem: exists,
        pendingCustom: { itemNumber, description, vendor, quantity },
        countedByName: counter?.full_name || exists.last_updated_name || 'another user',
      });
      setShowCustomModal(false);
      return;
    }
    await ensureInProgress();
    const loc = (locationOverride || location || '').trim().toUpperCase() || null;
    const nextSeq = locationOverride
      ? items.filter(i => i.item_number_raw?.toUpperCase() === itemNum).length + 1
      : 1;
    const { data: newItem } = await supabase.from('count_line_items').insert({
      inventory_count_id: countId,
      item_number_raw: itemNum,
      description_raw: description.trim().toUpperCase(),
      vendor_raw:      vendor.trim().toUpperCase(),
      quantity: quantity || 0, is_new_item: true, not_in_catalog: true,
      location: loc, location_seq: nextSeq,
    }).select().single();
    if (newItem) {
      setItems(prev => [...prev, newItem].sort((a, b) => {
        const cmp = a.item_number_raw?.localeCompare(b.item_number_raw);
        if (cmp !== 0) return cmp;
        return (a.location_seq || 1) - (b.location_seq || 1);
      }));
      setCatalogResults([]); setDescSearch('');
      setShowCustomModal(false); setCustomModalValue('');
      setDupeModal(null);
      toast.success('Custom item added.' + (loc ? ' (' + loc + ')' : ''));
    }
  }

  /*
    **A blank is not a zero, and this is where the difference is kept.**

    Two faults lived in the twelve lines this replaces, and both cost a rep work.

    **A rep could type 0 on a line nobody had counted and nothing was saved.**
    The old code read the current quantity as `item.quantity ?? 0`, so a line
    never counted looked like a line already counted as none. Typing 0 was
    therefore "no change", the function returned, and the database still said
    never counted while the screen showed a confident 0. The rep had done the
    work and OnHand had not recorded it. It compares against the real value now,
    null and all.

    **And clearing the box wrote 0 rather than clearing the line.**
    `parseInt('')` is not a number and `|| 0` turned that into zero, so a rep who
    deleted a wrong entry meaning to come back to it had silently counted that
    shelf as empty. An empty box means "I have not counted this" and is saved as
    exactly that.
  */
  /*
    **This write was not checked at all until 10 September 2026**, and it is the
    most important write in OnHand. `Promise.all` was awaited and its answer
    thrown away, so a refused or dropped quantity left the number sitting
    confidently on the screen and nothing in the database. **The rep had done
    the work and OnHand had not recorded it**, which is the fault named as the
    floor under the whole of job 25's part two: nothing about working offline
    helps until a write says whether it landed.

    **Three outcomes now, not one**, and telling them apart is the job:

      it landed         nothing to say, the screen already shows it
      no signal         held on the phone and sent later, and the bar says so
      refused           said out loud, and the number comes off the screen

    **The third is why a refusal is not queued.** A write the database refuses
    will be refused again in an hour and in a week, so holding it would give a
    rep a bar that never clears and a number that never lands.
  */
  /*
    **It says what happened, since rebrand step 5** ('same', 'held', 'saved' or
    'refused'), so the quick entry bar can say "Saved" only when it was. What it
    does is unchanged.
  */
  async function updateQty(itemId, newQty) {
    const emptied = newQty === '' || newQty === null || newQty === undefined;
    const qty  = emptied ? null : Math.max(0, parseInt(newQty, 10) || 0);
    const item = items.find(i => i.id === itemId);
    if (!item) return undefined;
    const oldQty = item.quantity ?? null;
    if (qty === oldQty) return 'same'; // genuinely no change

    setItems(prev => prev.map(i => i.id === itemId ? { ...i, quantity: qty } : i));
    await ensureInProgress();
    // The moment it was TYPED. A count taken at nine in a basement and sent at
    // eleven in the car park is a nine o'clock count.
    const now = new Date().toISOString();
    // first_count when this is the first number ever put on a line, an edit
    // otherwise. A line never counted has no old quantity at all.
    const changeType = (oldQty === null) ? 'first_count' : 'quantity_edit';

    const job = {
      itemId, countId, qty, oldQty, changeType, at: now,
      itemNumber: item.item_number_raw,
      description: item.description_raw,
      userId: profile?.id,
      userName: profile?.full_name || '',
    };

    /*
      **Once anything is waiting, everything waits.** If a rep types 5 on a line
      and it is held, then types 7 on the same line and that one goes straight
      through, the database ends up saying 5. So a queue that is not empty takes
      every later number rather than being overtaken. Same for a device that
      already knows it has no signal: there is no point asking.
    */
    if (outbox.isOffline() || outbox.anythingWaiting()) {
      outbox.hold(job);
      return 'held';
    }

    /*
      **What a save has to prove**, 18 September 2026 (the audit after the
      rebrand, issue 1 and a finding of its own):

      - **The line really changed.** A write no rule allows comes back with no
        error and changes nothing, so a manager at a regional office typed a
        number, saw it saved, and lost it on reload. The line now reports which
        rows it changed, and none is a refusal.
      - **Who changed it**, in `last_updated_by`, which is what the "who else is
        counting" band and each line's name chip read. Only `updated_by` was
        ever written, so the band always said nobody else was counting.
      - **A refused history row is not a refused number.** Clearing a number
        back to blank wrote a history row with no new number, which the
        database refused, and the screen put the old number back while the
        database already held the blank. The line is what the count is; its
        history is a record of it.
    */
    let err = null;
    try {
      const [line, history] = await Promise.all([
        supabase.from('count_line_items').update({
          quantity: qty,
          updated_by: profile?.id,
          last_updated_by: profile?.id,
          last_updated_at: now,
          last_updated_name: profile?.full_name || '',
        }).eq('id', itemId).select('id'),
        supabase.from('count_line_item_history').insert({
          line_item_id:       itemId,
          inventory_count_id: countId,
          item_number:        item.item_number_raw,
          description:        item.description_raw,
          old_quantity:       oldQty,
          new_quantity:       qty,
          changed_by:         profile?.id || null,
          changed_by_name:    profile?.full_name || '',
          changed_at:         now,
          change_type:        changeType,
        }),
      ]);
      const lineChanged = !line.error && Array.isArray(line.data) && line.data.length > 0;
      err = line.error
        || (!lineChanged ? { message: 'That line could not be changed.' } : null)
        || (history.error && outbox.looksLikeConnection(history.error) ? history.error : null);
      if (history.error && !outbox.looksLikeConnection(history.error)) {
        trace('countHistoryRefused', { itemId, message: history.error.message });
      }
      /* The line landed and the history did not for want of a signal. Holding
         the whole job would write the line twice, which is harmless, and the
         history once, which is what is wanted, so it is held with its first
         half already marked. */
      if (lineChanged && history.error) job.sentLine = true;
    } catch (e) {
      err = e;
    }

    if (!err) { flashSaved(itemId); return 'saved'; }

    if (outbox.looksLikeConnection(err)) {
      outbox.hold(job);
      return 'held';
    }

    /*
      **Refused. Say so, and take the number back off the screen**, because a
      number on screen that is not in the database is exactly the lie this whole
      job exists to stop. Rule 35 says never hand somebody what went wrong
      inside OnHand, so the message says what happened to their count.
    */
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, quantity: oldQty } : i));
    toast.error('That count was not saved, so the line has been put back as it was. Try again, and tell whoever looks after OnHand if it keeps happening.');
    return 'refused';
  }

  // Helper to write an audit row to count_line_item_history for non-quantity changes.
  // Quantity changes use their own inline pattern in addCatalogItem and updateQty.
  // For non-qty changes we set new_quantity to the item's current quantity so
  // there's context for "what was the quantity when this note/flag change happened".
  async function logHistory(item, changeType, noteText) {
    if (!item) return;
    try {
      const { error } = await supabase.from('count_line_item_history').insert({
        line_item_id:       item.id,
        inventory_count_id: countId,
        item_number:        item.item_number_raw,
        description:        item.description_raw,
        old_quantity:       item.quantity ?? null,
        new_quantity:       item.quantity ?? 0,
        notes:              noteText || null,
        changed_by:         profile?.id || null,
        changed_by_name:    profile?.full_name || '',
        changed_at:         new Date().toISOString(),
        change_type:        changeType,
      });
      if (error) warnHistory(error);
    } catch (e) { warnHistory(e); }
  }

  async function saveNote(itemId, note) {
    const item = items.find(i => i.id === itemId);
    const oldNote = item?.notes || '';
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, notes: note } : i));
    /*
      **Checked, 18 September 2026** (the audit after the rebrand, issue 11):
      this and the two flags below wrote without looking at the answer and said
      "saved" either way. A refused note now says so, the line goes back to what
      the database holds, and the note window stays open with the words in it.
    */
    const ok = await save(toast, () => supabase.from('count_line_items').update({ notes: note }).eq('id', itemId), 'the note');
    if (!ok) {
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, notes: oldNote } : i));
      return;
    }
    // History: note_edit with before/after stored in notes column
    if (item && oldNote !== note) {
      logHistory(item, 'note_edit', 'OLD: ' + (oldNote || '(none)') + '\nNEW: ' + (note || '(cleared)'));
    }
    setNoteModal(null);
    toast.success('Note saved.');
  }

  async function saveItemEdit(itemId, { itemNum, desc, vendor }) {
    const before = items.find(i => i.id === itemId);
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, item_number_raw: itemNum, description_raw: desc, vendor_raw: vendor } : i));
    /*
      **Said so when the database refuses.** This wrote, threw the answer away
      without looking at it, and told the person "Item updated" either way, which
      is the fault the whole of `src/lib/save.js` exists for: a refused write
      that reports success looks right until the page is reloaded.
    */
    const ok = await save(toast,
      () => supabase.from('count_line_items').update({ item_number_raw: itemNum, description_raw: desc, vendor_raw: vendor }).eq('id', itemId),
      'this line');
    if (!ok) {
      // Put the screen back to what the database actually holds rather than
      // leaving somebody looking at a change that did not happen.
      if (before) setItems(prev => prev.map(i => i.id === itemId ? before : i));
      return;
    }
    logHistory(before, 'item_edit',
      'OLD: ' + [before?.item_number_raw, before?.description_raw, before?.vendor_raw].filter(Boolean).join(' | ') +
      '\nNEW: ' + [itemNum, desc, vendor].filter(Boolean).join(' | '));
    setEditModal(null);
    toast.success('Line updated.');
  }

  async function toggleDeadStock(itemId) {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const newVal = !item.is_dead_stock;
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, is_dead_stock: newVal } : i));
    if (!await save(toast, () => supabase.from('count_line_items').update({ is_dead_stock: newVal }).eq('id', itemId), 'the dead stock flag')) {
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, is_dead_stock: !newVal } : i));
      return;
    }
    logHistory(item, 'flag_dead_stock', newVal ? 'Flagged as dead stock' : 'Dead stock flag removed');
    toast.success(newVal ? 'Flagged as dead stock.' : 'Dead stock flag removed.');
  }

  async function toggleNonInventory(itemId) {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const newVal = !item.is_non_inventory;
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, is_non_inventory: newVal } : i));
    if (!await save(toast, () => supabase.from('count_line_items').update({ is_non_inventory: newVal }).eq('id', itemId), 'the non-inventory flag')) {
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, is_non_inventory: !newVal } : i));
      return;
    }
    logHistory(item, 'flag_non_inventory', newVal ? 'Flagged as non-inventory' : 'Non-inventory flag removed');
    toast.success(newVal ? 'Flagged as non-inventory.' : 'Non-inventory flag removed.');
  }

  async function removeItem(itemId) {
    // FK line_item_id has ON DELETE CASCADE, so history rows for this item
    // auto-delete with the line item. No need to write a removal history row,
    // it would just cascade-delete immediately. By design, removed items lose
    // their audit trail (which is fine for our use case, since removed items usually
    // mean "should never have been here" rather than meaningful edits).
    /*
      **Checked for a line actually removed**, 18 September 2026 (the audit
      after the rebrand, issue 8). Only an admin could remove a line, and a
      refused delete comes back with no error, so a rep pressing this was told
      "Item removed." and the line came back on reload. The owner then decided
      reps may remove lines (question 4), and the database allows it for
      whoever is counting the sheet; a removal that removes nothing is still
      said as such.
    */
    const { data: gone, error } = await supabase.from('count_line_items').delete().eq('id', itemId).select('id');
    if (error || !gone || gone.length === 0) {
      toast.error('Could not remove that line, so it is still on the sheet.' + (error?.message ? ' ' + error.message : ''));
      return;
    }
    setItems(prev => prev.filter(i => i.id !== itemId));
    setRemoveModal(null);
    toast.success('Item removed.');
  }

  // The one shelf-location save, shared by laptop, tablet and phone. It writes the
  // count line rather than item_locations, so it is keyed by row id (duplicates of
  // the same item stay independent) and a rep may write it, which they may not do
  // on item_locations. Do not point a layout back at item_locations.
  async function saveRowLocation(itemId, itemNumber, locationText) {
    const trimmed = locationText.trim().toUpperCase();
    const { error } = await supabase.from('count_line_items')
      .update({ location: trimmed || null }).eq('id', itemId);
    if (error) {
      toast.error('Could not save that location: ' + (error.message || 'unknown error'));
      return;
    }
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, location: trimmed || null } : i));
    setEditingLocation(null);
    setLocationDraft('');

    // Remember the position against the location itself, so next quarter's count
    // opens with it already filled in. This goes through set_item_shelf_location
    // rather than writing item_locations directly, because a rep may not write
    // that table: the function takes this line and the text, and works out the
    // location and the item number for itself. Clearing the box does not erase
    // what was remembered, so nothing here runs on a blank.
    if (!trimmed) return;
    const { error: memoryError } = await supabase.rpc('set_item_shelf_location', {
      p_line_id: itemId,
      p_location: trimmed,
    });
    if (memoryError) {
      // The position is saved on this count either way. Only the carry-forward
      // to next quarter is missing, so say so without implying the save failed.
      toast.warning('Saved on this count, but it will not carry forward: ' +
        (memoryError.message || 'unknown error'));
    }
  }

  function handleSort(col) {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }

  async function handleScanConfirm(scanResult, qty, locationOverride) {
    const existing = items.find(i => i.item_number_raw === scanResult.item_number);
    if (existing && !locationOverride) {
      const { data: counter } = await supabase.from('profiles').select('full_name').eq('id', existing.last_updated_by || existing.updated_by || '').maybeSingle();
      setDupeModal({ existingItem: existing, pendingCatalogItem: { ...scanResult, scanQty: qty }, countedByName: counter?.full_name || existing.last_updated_name || 'another user' });
      // Only close the Dynamsoft modal. ZXing handles duplicates inline.
      if (showScanModal) setShowScanModal(false);
      return;
    }
    if (existing && locationOverride) {
      // Add new location entry
      const nextSeq = items.filter(i => i.item_number_raw === scanResult.item_number).length + 1;
      const { data: newItem } = await supabase.from('count_line_items').insert({
        inventory_count_id: countId, ...lineLink(scanResult),
        item_number_raw: scanResult.item_number, description_raw: scanResult.description,
        vendor_raw: scanResult.vendor, quantity: qty,
        is_new_item: true, not_in_catalog: false, entered_via_scan: true,
        location: locationOverride, location_seq: nextSeq,
      }).select().single();
      // A refused scan used to say "Added" too (18 September 2026).
      if (!newItem) { toast.error('Could not add ' + scanResult.item_number + ' to this sheet. Nothing was added.'); return; }
      setItems(prev => [...prev, newItem].sort((a, b) => {
        const cmp = a.item_number_raw?.localeCompare(b.item_number_raw);
        if (cmp !== 0) return cmp;
        return (a.location_seq || 1) - (b.location_seq || 1);
      }));
      setDupeModal(null);
      toast.success('Added ' + scanResult.item_number + ' (' + locationOverride + ')');
      return;
    }
    const { data: newItem } = await supabase.from('count_line_items').insert({
      inventory_count_id: countId, ...lineLink(scanResult),
      item_number_raw: scanResult.item_number, description_raw: scanResult.description,
      vendor_raw: scanResult.vendor, quantity: qty,
      is_new_item: true, not_in_catalog: false, entered_via_scan: true,
      location: null, location_seq: 1,
    }).select().single();
    if (!newItem) { toast.error('Could not add ' + scanResult.item_number + ' to this sheet. Nothing was added.'); return; }
    setItems(prev => [...prev, newItem]);
    toast.success('Added ' + scanResult.item_number);
  }

  async function saveProgress() {
    setSaving(true);
    // Checked (18 September 2026): it said "Progress saved." whatever happened.
    const ok = await saveChanged(toast, () => supabase.from('inventory_counts')
      .update({ status: 'in_progress' }).eq('id', countId).select('id'), 'your progress');
    setSaving(false);
    if (ok) toast.success('Progress saved.');
  }

  async function ensureCountApprovalTodo() {
    // Check if a todo already exists for this count (edge function may have created one)
    const { data: existing } = await supabase
      .from('todos')
      .select('id')
      .eq('count_id', countId)
      .eq('todo_type', 'count_approval')
      .eq('is_complete', false)
      .maybeSingle();
    if (existing) return; // already exists, don't duplicate

    await supabase.from('todos').insert({
      title:       'Count to approve: ' + (count?.warehouse?.name || ''),
      description: 'Rep ' + (profile?.full_name || '') + ' submitted a count for ' + (count?.warehouse?.name || '') + ' and it is ready for review.',
      priority:    'normal',
      todo_type:   'count_approval',
      warehouse_id: count?.warehouse?.id || null,
      rep_id:      profile?.id || null,
      count_id:    countId,
      is_complete: false,
      metadata:    JSON.stringify({
        rep_name:     profile?.full_name || '',
        rep_email:    profile?.email || '',
        location_name: count?.warehouse?.name || '',
        region:        count?.warehouse?.region?.name || '',
        submitted_at: new Date().toISOString(),
      }),
    });
  }

  async function submitCount() {
    setSubmitting(true); setShowSubmitModal(false);
    // Sent through the server, and told truthfully whether it went:
    // src/pages/countEntry/submitSheet.js (the audit after the rebrand, 18
    // September 2026). Nothing below runs unless it really was sent in.
    const outcome = await sendSheetIn({
      supabase, toast, countId,
      callServer: async () => {
        // Call Edge Function - handles submit, Excel build, and email
        const { data: { session } } = await supabase.auth.getSession();
        return fetch(
          'https://uscjyqgfncqoqqegrcjw.supabase.co/functions/v1/submit-count',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + session?.access_token,
            },
            body: JSON.stringify({
              count_id:  countId,
              rep_email: profile?.email || '',
              rep_name:  profile?.full_name || '',
            }),
          }
        );
      },
    });
    if (outcome !== 'submitted') { setSubmitting(false); return; }
    /*
      **Submitting a count left no trace in the audit log**, which is the one
      question that log exists to answer. Written here rather than inside the
      two branches above because a count is submitted either way: the second
      branch is the fallback that marks it submitted when the email service is
      unreachable.
    */
    await logAudit(profile, 'COUNT_SUBMITTED', 'count',
      { target_type: 'count', target_name: count?.warehouse?.name || 'a location',
        details: { count_id: countId, items: items.length } });

    /*
      **The variance warning, built 5 September 2026 on the owner's decision.**

      Site Settings has had a "variance threshold" box since it was written. It
      saved correctly and **nothing anywhere read it**, so an administrator who
      set one reasonably believed something would happen. Nothing did: no alert
      of this kind had ever been raised.

      Submitting is the right moment for it. It is when the numbers are final,
      when there is somebody to tell, and when an administrator is about to look
      at the sheet anyway.

      **One notification per sheet, not one per line.** A location whose whole
      stock has turned over would otherwise send forty, and forty notifications
      about one sheet is the same as none.
    */
    const { variancePct } = await countRules();
    const changed = bigChanges(items, variancePct);
    if (changed.length) {
      const worst = changed.slice().sort((a, b) => b.pct - a.pct)[0];
      await notify(toast, {
        alert_type: 'high_variance',
        title: 'Big change since last count',
        message: changed.length + ' item' + (changed.length === 1 ? '' : 's') + ' at '
          + (count?.warehouse?.name || 'this location') + ' changed by more than '
          + variancePct + '% since last quarter. The largest is ' + worst.item
          + ', ' + worst.before + ' to ' + worst.after + '.',
        rep_id: profile?.id || null,
        inventory_count_id: countId,
      });
    }

    // Always ensure a count_approval todo exists for the admin
    await ensureCountApprovalTodo();
    navigate('/');
    setSubmitting(false);
  }

  async function flagForClosure() {
    if (!closureForm.reason) { toast.error('Please select a reason for closure.'); return; }
    // Both writes checked, 18 September 2026: it said "Admin notified." whether
    // or not either was stored.
    if (!await save(toast, () => supabase.from('inventory_counts').update({
      closure_reason: closureForm.reason, closure_notes: closureForm.notes,
      closure_flagged_at: new Date().toISOString(),
    }).eq('id', countId), 'the closure flag')) return;
    if (!await save(toast, () => supabase.from('todos').insert({
      title: 'Location flagged for closure: ' + count?.warehouse?.name,
      description: 'Rep ' + (profile?.full_name || '') + ' flagged ' + count?.warehouse?.name + ' for closure. Reason: ' + closureForm.reason,
      priority: 'high', todo_type: 'location_closure',
      warehouse_id: count?.warehouse?.id, rep_id: profile?.id || null, count_id: countId, is_complete: false,
      metadata: JSON.stringify({ ...closureForm, rep_name: profile?.full_name || '', location_name: count?.warehouse?.name || '', region: count?.warehouse?.region?.name || '' }),
    }), 'the request to the admins')) return;
    /*
      **This notification was refused by the database every single time**, and
      nothing here looked. It is called `location_closure_flagged` and the
      database's fixed list of names only had the older `account_closure_flagged`
      on it, from before counting moved to locations. So the rep saw "Admin
      notified" and no administrator was ever notified.

      Fixed 5 September 2026: the name is on the list, this goes through the one
      place that checks the answer, and it now carries the count sheet it is
      about so that an administrator can open the thing being talked about, and
      so that a manager covering that region can see it at all.
    */
    await notify(toast, {
      alert_type: 'location_closure_flagged',
      title: 'Location flagged for closure',
      message: count?.warehouse?.name + ' flagged for closure by ' + (profile?.full_name || 'a rep'),
      inventory_count_id: countId,
    });
    setShowClosureModal(false);
    toast.success('Location flagged for closure. Admin notified.');
  }

  const totalItems     = items.length;
  const totalUnits     = items.reduce((sum, i) => sum + (i.quantity || 0), 0);
  /*
    **How much is left, which the screen never said.** The approved drawing puts
    it beside the totals at every size, and it is what makes "blank is not zero"
    useful rather than merely correct: seeing that a line is blank tells a rep
    about one shelf, and this tells them about the sheet.
  */
  const progress       = sheetProgress(items);
  /*
    **Who else is counting this location right now.** Only a regional or branch
    office allows two people at once, and there are two such places in the whole
    company. Read off the lines themselves: each one carries who last changed it
    and when, and the live feed keeps both up to date as other people save.
  */
  const others      = isWarehouse ? otherCounters(items, profile?.id) : [];
  const sharedSays  = sharedSentence(others);
  const flagged        = items.filter(i => i.not_in_catalog).length;
  const inventoryValue = items.reduce((sum, i) => {
    const cost = i.item?.cost_per_item || 0;
    return sum + (i.quantity || 0) * cost;
  }, 0);
  const fmtValue = inventoryValue >= 1000
    ? '$' + (inventoryValue / 1000).toFixed(1) + 'k'
    : '$' + Math.round(inventoryValue);

  const sortedItems = [...items].sort((a, b) => {
    // Always group by item number first, then by location_seq within the same item
    const aNum = a.item_number_raw || '';
    const bNum = b.item_number_raw || '';
    if (aNum !== bNum) {
      // Sort the primary item by whatever column is selected
      let aVal = sortCol === 'location' ? (a.location || '') : (a[sortCol] || '');
      let bVal = sortCol === 'location' ? (b.location || '') : (b[sortCol] || '');
      // For item number grouping use the item_number_raw regardless of sort col
      aVal = sortCol === 'item_number_raw' ? aNum : aVal;
      bVal = sortCol === 'item_number_raw' ? bNum : bVal;
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    // Same item number
    return (a.location_seq || 1) - (b.location_seq || 1);
  });


  /*
    **Everything the one list needs, handed to it once.** Both shells render the
    same `<LineList>`: the compact one below 1024 and the desk one above it. It
    was three separate lists before, which is what this job is named after.
  */
  /*
    **The filter chips on a phone and a tablet** (rebrand step 5, guide 12.3):
    all the lines, the ones still to count, or the ones counted. They only
    choose which lines are shown, from the lines already loaded.
  */
  const LINE_FILTERS = [
    { key: 'all',     label: 'All',      keep: () => true },
    { key: 'todo',    label: 'To count', keep: i => isUncounted(i) },
    { key: 'counted', label: 'Counted',  keep: i => !isUncounted(i) },
  ];
  const shownItems = isCompact
    ? sortedItems.filter((LINE_FILTERS.find(f => f.key === lineFilter) || LINE_FILTERS[0]).keep)
    : sortedItems;
  const chosenId = shownItems.some(i => i.id === selectedId) ? selectedId : (shownItems[0]?.id ?? null);

  // The quick entry bar found a line: light it and bring it into view.
  function showFound(line) {
    setActiveId(line.id);
    const row = [qtyRefs.current[line.id + ':wide']].find(Boolean);
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
  }
  // The quick entry bar saves through the one save, then flashes the row.
  async function quickSave(line, n) {
    const outcome = await updateQty(line.id, n);
    if (outcome === 'saved' || outcome === 'same') {
      setFlashId(line.id);
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlashId(null), 1400);
    }
    return outcome;
  }

  const sortChanged = !(sortCol === 'item_number_raw' && sortDir === 'asc');
  const sortSays    = sortLabel(sortCol, sortDir) || 'A custom order';

  const lineListProps = {
    items: shownItems,
    myId: profile?.id,
    shared: isWarehouse,
    locationEnabled,
    sort: { col: sortCol, dir: sortDir },
    onSort: handleSort,
    openMenu: actionMenu,
    onToggleMenu: id => setActionMenu(cur => (cur === id ? null : id)),
    actions: {
      edit:         item => setEditModal(item),
      note:         item => setNoteModal(item),
      nonInventory: item => toggleNonInventory(item.id),
      deadStock:    item => toggleDeadStock(item.id),
      remove:       item => setRemoveModal(item),
    },
    qtyRef:     (id, where, el) => { qtyRefs.current[id + ':' + where] = el; },
    onQtyFocus: (id, e) => {
      focusedLine.current = id;
      setActiveId(id);
      // What Escape puts back: the number the box had when the cursor went in.
      const line = items.find(i => i.id === id);
      focusedValue.current[id] = line ? (line.quantity ?? null) : null;
      // Selected, so the next number typed replaces it (guide 12.3).
      if (e && e.target && typeof e.target.select === 'function') e.target.select();
    },
    onQtyBlur:  id => { if (focusedLine.current === id) focusedLine.current = null; },
    onQtyKey:   (e, item) => onQtyKey(e, shownItems, shownItems.findIndex(i => i.id === item.id), item),
    onQty:      (id, v) => updateQty(id, v),
    savedId,
    activeId,
    flashId,
    invalidId,
    editingLocation,
    locationDraft,
    setLocationDraft,
    onLocationOpen:   item => { setEditingLocation(item.id); setLocationDraft(item.location || ''); },
    onLocationSave:   item => saveRowLocation(item.id, item.item_number_raw, locationDraft),
    onLocationCancel: () => { setEditingLocation(null); setLocationDraft(''); },
    empty: 'No lines on this sheet yet. Search above to add one.',
  };

  const showCatalogDropdown = descSearch.length >= 2 && (catalogResults.length > 0 || searching);
  const showAddCustom = descSearch.length >= 2 && !searching && catalogResults.length === 0;

  /*
    **A count sheet loading is the case the owner chose shape B for**, in his
    own example: a rep on a slow connection in a supply room, waiting to see
    whether a list is coming. So this is the outline of the lines rather than a
    spinner, and it keeps the shell, rule 35.

    **Twelve rows is not a promise about this sheet.** It is what fills the
    screen; a real sheet holds sixty. The number is capped inside the component
    at what a screen can show, so the outline never claims a length it does not
    know.
  */
  if (loading) return (
    <SystemShell band="Count sheet">
      {/* The side gutter the real screen has. Below 1024 the shell hands its
          children the full width, which is right for a stop card because that
          card centres itself, and wrong for an outline that fills the width:
          the first picture had it flush against both edges of the phone. */}
      <div className="skel-hold"><PageLoading rows={12} /></div>
    </SystemShell>
  );

  /*
    **Somebody else has this count sheet open.**

    **Rebuilt 10 September 2026 for job 25**, to Group H's approved drawing. It
    was a dead end: a card on a blank background with one button, Go Back, and
    no way to do anything about it at all.

    **The owner was asked whether a rep should be able to ask for the sheet or
    be told when it frees, and he said "maybe both."** So it has both.

    **"Ask for it now" is not a new mechanism.** OnHand already has an ownership
    request that goes to an administrator, used when another rep OWNS a sheet.
    This is the case where somebody HAS IT OPEN right now, which is a different
    lock and the same ask, so it goes through the request that exists rather
    than a second one built beside it.

    **And it keeps the shell**, rule 35: you are signed in, the app is fine, and
    the menu is the way out. Taking it away is what made this a dead end.
  */
  if (sessionBlocked) {
    return (
      <SystemShell band={count?.warehouse?.name || 'Count sheet'} under={count?.cycle?.name}>
        <StopCard
          mark="shut"
          tone="gold"
          title={(blockedByName || 'Somebody else') + ' has this count open'}
          says={'Two people counting one sheet at the same time overwrite each other, so OnHand holds it for whoever got there first. It is released 30 minutes after they last touch it.'}
          facts={[
            { value: ago(blockedSince) || 'just now', label: 'Open for' },
            { value: '30 min', label: 'Freed after' },
          ]}
          alt={{ label: 'Ask for it now', onClick: () => setShowTransferModal(true) }}
          act={{
            label: watchingSheet ? 'You will be told' : 'Tell me when it is free',
            disabled: watchingSheet,
            onClick: () => {
              /* **It answers whether the device will remember**, so a rep is
                 never shown a tick over nothing. A watch nobody can keep is
                 worse than no watch, because they stop checking. */
              if (sheetWatch.watch(countId, count?.warehouse?.name)) {
                setWatchingSheet(true);
                toast.success('You will be told when this sheet is free, as long as OnHand is open.');
              } else {
                toast.error('This device will not remember that, so OnHand cannot tell you. Try again from a different browser, or ask for the sheet instead.');
              }
            },
          }}
        />
        {/* The request that already exists, opened from the card rather than
            built again beside it. */}
        {showTransferModal && (
          <OwnershipTransferModal
            count={count}
            profile={profile}
            ownerName={blockedByName}
            toast={toast}
            onClose={() => setShowTransferModal(false)}
          />
        )}
      </SystemShell>
    );
  }

  // Ownership locked -- another rep already claimed this count
  if (ownershipLocked && count) {
    const banner = (
      <>
        {showTransferModal && (
          <OwnershipTransferModal
            count={count}
            profile={profile}
            ownerName={ownerName}
            onClose={() => setShowTransferModal(false)}
            onSuccess={() => {}}
            toast={toast}
          />
        )}
        <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-bg)', borderRadius: 'var(--oh-radius-control)', padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flexShrink: 0, width: 32, height: 32, borderRadius: '50%', background: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ stroke: 'var(--warning)' }} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--warning-ink)', marginBottom: 2 }}>View Only - Count In Progress by {ownerName}</div>
            <div style={{ fontSize: 13, color: 'var(--warning-ink)', lineHeight: 1.5, marginBottom: 10 }}>
              {ownerName} has already started this count. You can request admin to transfer ownership to you.
            </div>
            <button
              onClick={() => setShowTransferModal(true)}
              style={{ fontSize: 12, fontWeight: 700, padding: '7px 16px', borderRadius: 'var(--oh-radius-chip)', background: 'var(--action)', border: 'none', color: 'var(--on-action)', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Request Ownership Transfer
            </button>
          </div>
        </div>
      </>
    );
    return (
      <LockedView
        count={count} items={items}
        totalItems={totalItems} totalUnits={totalUnits} flagged={flagged}
        navigate={navigate}
        ownerBanner={banner}
      />
    );
  }

  // Locked / read-only view
  if (count?.status === 'submitted' || count?.status === 'approved') {
    return (
      <LockedView
        count={count} items={items}
        totalItems={totalItems} totalUnits={totalUnits} flagged={flagged}
        navigate={navigate}
      />
    );
  }

  //  TABLET / MOBILE LAYOUT 
  if (isCompact) {
    return (
      /* No ground of its own since theme phase 3, 14 September 2026: the shared
         frame's still background shows behind the count sheet on a phone too.
         It painted solid Paper until then, so the moving background never
         showed here at all. */
      <div style={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>

        {/* Floating glass header card (Phase 4: mockup mt) */}
        <div ref={mobHeaderRef} className="oh-bar" style={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 6, borderRadius: 'var(--oh-radius-dialog)', padding: isTablet ? '16px 20px' : '13px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                  {count?.status === 'not_started' ? 'Not started' : count?.status === 'in_progress' ? 'Counting' : count?.status === 'submitted' ? 'Submitted' : 'Approved'}
                </span>
                {/* **"Live" said nothing about what was live.** The word was
                    the only sign on any screen that another person could be in
                    this sheet, and it never named them. */}
                {isWarehouse && <span className="chip-shared">Shared</span>}
              </div>
              <div style={{ fontSize: isTablet ? 22 : 19, fontWeight: 800, color: 'var(--action-text)', lineHeight: 1.1, letterSpacing: -0.4 }}>{count?.warehouse?.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{count?.cycle?.name}</div>
            </div>
            <button className="flag-closed" onClick={() => setShowClosureModal(true)} style={{ flexShrink: 0, background: 'var(--critical-bg)', border: 'none', borderRadius: 'var(--oh-radius-control)', padding: '7px 11px', color: 'var(--critical-ink)', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Flag closed</button>
          </div>

          {/* **How much is done, on a phone and a tablet** (guide 12.3): "X of Y
              items counted" and a bar the width of the card. It is the number a
              rep opens the sheet for, so it stays in the header while the list
              scrolls. The totals and the Item Locations switch scroll with the
              page since rebrand step 5, so the list gets the room. */}
          <div className="ce-progress">
            <div className="ce-progress-words">
              <span><b>{progress.done} of {progress.total}</b> items counted</span>
              {progress.left > 0 && <span className="ce-progress-left">{progress.left} left</span>}
            </div>
            <div className="ce-progress-bar" aria-hidden="true">
              <i style={{ width: (progress.total ? Math.round(progress.done / progress.total * 100) : 0) + '%' }} className={progress.total && progress.done === progress.total ? 'is-done' : ''} />
            </div>
          </div>
        </div>

        {/* Scrollable body: content slides under the glass header, above the dock */}
        <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', paddingTop: mobHeaderH + 24, paddingBottom: 100, zIndex: 1, '--ce-sticky-top': (mobHeaderH + 24) + 'px' }} onClick={() => setActionMenu(null)}>

        {/* **The connection bar, and the phone is the whole reason it exists.**
            A rep counts in a hospital supply room or a basement with a phone in
            one hand and a shelf in the other, which is the case Group H's
            offline drawings are all drawn at 390.

            **It was in the desk tree only on the first build**, which is the
            same fault as everything else with two trees in this file: count
            entry returns a whole separate layout below 1024, and a component
            added to one of them is missing from the other with nothing saying
            so. Caught by photographing it at 390, where it was not. */}
        <div style={{ padding: isMobile ? '4px 12px 0' : '8px 16px 0' }}>
          <ConnectionBar />
        </div>

        {/* The sheet's totals and the Item Locations switch. */}
        <div className="ce-top-tools" style={{ padding: isMobile ? '4px 12px 8px' : '8px 16px 10px' }}>
          {/* Stat tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 10 }}>
            {[
              { val: totalItems, lbl: 'Lines',       color: 'var(--action-text)' },
              { val: totalUnits, lbl: 'Total Units',  color: 'var(--accent-ink)' },
              { val: fmtValue,   lbl: 'Est. Value',   color: 'var(--success-ink)' },
            ].map(s => (
              <div key={s.lbl} style={{ borderRadius: 'var(--oh-radius-card)', padding: '10px 8px', background: 'var(--surface-card)', boxShadow: 'var(--depth-rest)', border: '1px solid var(--border)', textAlign: 'center' }}>
                <div style={{ fontSize: isTablet ? 22 : 19, fontWeight: 800, color: s.color, lineHeight: 1, letterSpacing: -0.5 }}>{s.val}</div>
                <div style={{ fontSize: 8.5, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: 0.8, textTransform: 'uppercase', marginTop: 6, whiteSpace: 'nowrap' }}>{s.lbl}</div>
              </div>
            ))}
          </div>

          {/* Location toggle */}
          <div {...pressable(toggleItemLocations, { role: 'switch', 'aria-checked': !!locationEnabled })} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, padding: '8px 12px', minHeight: 44, boxSizing: 'border-box', borderRadius: 'var(--oh-radius-control)', background: 'var(--surface-card)', boxShadow: 'inset 0 0 0 1px var(--border)', cursor: 'pointer' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Item Locations</span>
            <div style={{ width: 34, height: 18, borderRadius: 'var(--oh-radius-control)', background: locationEnabled ? 'var(--accent)' : 'var(--border-strong)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
              <div style={{ position: 'absolute', top: 2, left: locationEnabled ? 18 : 2, width: 14, height: 14, borderRadius: '50%', background: 'var(--surface-card)', transition: 'left 0.2s' }} />
            </div>
          </div>
        </div>

        {/* **The shared location band, on a phone and a tablet too.** It sits
            here rather than in the floating header card, because the card is
            already the tallest thing on the screen and everything under it is
            pushed down by however tall it is. */}
        {isWarehouse && (
          <div style={{ padding: isMobile ? '4px 12px 0' : '8px 16px 0' }}>
            <SharedBand says={sharedSays} live={others.length > 0} />
          </div>
        )}

        {/* Search bar + Sort button */}
        <div style={{ padding: isMobile ? '4px 12px 8px' : '8px 16px 10px' }}>
          <div style={{ display: 'flex', gap: 6, position: 'relative' }}>
            <input
              ref={searchRef}
              value={descSearch}
              onChange={e => setDescSearch(e.target.value.toUpperCase())}
              placeholder="Add an item: number, name or vendor"
              aria-label="Add an item to this sheet"
              style={{ flex: 1, minWidth: 0, minHeight: 48, boxSizing: 'border-box', padding: isMobile ? '11px 14px' : '12px 15px', border: 'none', borderRadius: 'var(--oh-radius-card)', fontSize: isMobile ? 14 : 15, fontWeight: 700, fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--surface-data)', boxShadow: descSearch ? 'inset 0 0 0 2px var(--action)' : 'inset 0 0 0 1px var(--border)', outline: 'none' }}
            />
            {/* Sort button */}
            <button className="ce-tool"
              onClick={() => setShowSortSheet(true)}
              style={{ padding: isMobile ? '8px 10px' : '10px 14px', borderRadius: 'var(--oh-radius-control)', background: sortChanged ? 'var(--accent-bg)' : 'var(--surface-glass)', border: `1px solid ${sortChanged ? 'var(--accent)' : 'var(--border)'}`, color: sortChanged ? 'var(--accent-ink)' : 'var(--text-secondary)', fontSize: isMobile ? 11 : 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0, position: 'relative', whiteSpace: 'nowrap' }}>
              Order
              {sortChanged && (
                <span style={{ position: 'absolute', top: -3, right: -3, width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', border: '1.5px solid var(--surface-card)' }} />
              )}
            </button>

            <button className="ce-tool" onClick={() => setShowScanModal(true)} style={{ minHeight: 'var(--oh-tap-min)', padding: isMobile ? '0 15px' : '0 18px', borderRadius: 'var(--oh-radius-sm)', background: 'var(--surface-glass)', color: 'var(--text-primary)', border: '1.5px solid var(--border-strong)', fontSize: isMobile ? 14 : 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
              Scan
            </button>
            {/* Catalog dropdown */}
            {showCatalogDropdown && (
              <div style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', zIndex: 50, background: 'var(--surface-card)', border: '1.5px solid var(--action)', borderRadius: 12, boxShadow: 'var(--depth-lift)', overflow: 'hidden' }}>
                <div style={{ padding: '7px 14px', background: 'var(--info-bg)', borderBottom: '1px solid var(--info-bg)', fontSize: 11, fontWeight: 700, color: 'var(--info-ink)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between' }}>
                  <span>From Catalog</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{searching ? 'Searching...' : catalogResults.length + ' result' + (catalogResults.length !== 1 ? 's' : '')}</span>
                </div>
                {catalogResults.map(r => (
                  <div key={r.id} {...pressable(() => addCatalogItem(r))}
                    style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-soft)', background: 'var(--surface-data)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-data)'}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--action-text)' }}>{r.item_number}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.description}</div>
                    </div>
                    <button className="add-btn" style={{ fontSize: 14, padding: '7px 16px' }}>+ Add</button>
                  </div>
                ))}
              </div>
            )}
            {showAddCustom && (
              <div {...pressable(() => { setCustomModalValue(descSearch.toUpperCase()); setShowCustomModal(true); })}
                style={{ position: 'absolute', left: 0, right: 56, top: 'calc(100% + 4px)', padding: '10px 14px', background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 'var(--oh-radius-chip)', cursor: 'pointer', fontSize: 13, color: 'var(--accent-ink)', fontWeight: 600, zIndex: 50 }}>
                + Add "{descSearch}" as custom item (not in catalog)
              </div>
            )}
          </div>
          {/* **One order for everybody.** A phone had its own, so the same
              person saw their lines in a different order depending on what they
              were holding, and the phone's ignored the grouping that keeps a
              line's second and third shelf under the first. */}
          {sortChanged && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ fontSize: 10, color: 'var(--accent-ink)', fontWeight: 600 }}>{sortSays}</span>
              <button onClick={() => { setSortCol('item_number_raw'); setSortDir('asc'); }}
                style={{ fontSize: 10, fontWeight: 700, color: 'var(--link)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>Clear</button>
            </div>
          )}
        </div>

        {/* Admin override banner */}
        {adminOverriding && (
          <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', padding: isTablet ? '10px 18px' : '8px 14px', display: 'flex', alignItems: 'flex-start', gap: 10, flexShrink: 0 }}>
            <div style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ stroke: 'var(--warning-ink)' }} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--warning-ink)', lineHeight: 1.2 }}>Admin Override - Claimed by {ownerName}</div>
              <div style={{ fontSize: 11, color: 'var(--warning-ink)', lineHeight: 1.4, marginTop: 1 }}>
                Your changes will affect their count data.
              </div>
            </div>
          </div>
        )}

        {/* **One list, at every width.** There were three of these written out
            in full: a table on a desk, a narrower table on a tablet and a stack
            of cards on a phone, each with its own copy of the row menu and the
            quantity stepper. `countEntry/LineList.jsx` is the one definition
            and the stylesheet decides its shape. */}
        {/* The filter chips (guide 12.3): which lines to show. */}
        <div className="ce-chips" role="group" aria-label="Show lines" style={{ padding: isMobile ? '0 12px 10px' : '0 16px 12px' }}>
          {LINE_FILTERS.map(f => (
            <button key={f.key} type="button" className={'ce-chip' + (lineFilter === f.key ? ' on' : '')}
              aria-pressed={lineFilter === f.key} onClick={() => setLineFilter(f.key)}>
              {f.label}
              <span className="ce-chip-n">{sortedItems.filter(f.keep).length}</span>
            </button>
          ))}
        </div>

        {/* A phone counts down a list with a stepper on every line; a tablet
            chooses a line on the left and counts it on the right (guide 12.3). */}
        {isTablet ? (
          <div style={{ padding: '0 16px 8px' }}>
            <SplitView {...lineListProps} selectedId={chosenId} onSelect={chooseLine}
              empty={lineFilter === 'all' ? lineListProps.empty : 'No lines to show here.'} />
          </div>
        ) : (
          <div className="ce-lines" style={{ padding: '0 12px 8px' }}>
            <LineList {...lineListProps}
              empty={lineFilter === 'all' ? lineListProps.empty : 'No lines to show here.'} />
          </div>
        )}

        </div>{/* end scrollable body */}

        {/* The floating count bar (rebrand step 4, guide 11.9): the dock's same
            three buttons, with the same handlers, on the count bar's glass. */}
        <CountBar status={count?.status} progress={progress}>
          <button type="button" className="btn btn-outline ce-bar-back" onClick={() => navigate(-1)} title="Back to Dashboard" aria-label="Back to Dashboard">
            <ArrowLeft size={16} />
          </button>
          <button type="button" className="btn btn-outline" onClick={saveProgress} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setShowSubmitModal(true)} disabled={submitting}>
            Submit
          </button>
        </CountBar>

        {/* Sort bottom sheet */}
        {showSortSheet && (
          <div
            onClick={() => setShowSortSheet(false)}
            style={{ position: 'fixed', inset: 0, background: 'var(--scrim)', zIndex: 200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface-card)', borderRadius: '16px 16px 0 0', padding: '16px 20px 32px' }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 18px' }} />
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 12 }}>Order by</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {SORT_CHOICES.map(opt => {
                  const on = sortCol === opt.col && sortDir === opt.dir;
                  return (
                    <button
                      key={opt.col + opt.dir}
                      onClick={() => { setSortCol(opt.col); setSortDir(opt.dir); setShowSortSheet(false); }}
                      style={{
                        width: '100%', padding: '13px 16px', borderRadius: 'var(--oh-radius-control)', border: '1.5px solid',
                        borderColor: on ? 'var(--action)' : 'var(--border)',
                        background: on ? 'var(--selected)' : 'var(--hover)',
                        color: on ? 'var(--action-text)' : 'var(--text-primary)',
                        fontSize: 14, fontWeight: on ? 700 : 500,
                        cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                      {opt.label}
                      {on && <span style={{ color: 'var(--action-text)', display: 'flex' }}><Tick size={15} /></span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        {showSubmitModal  && <SubmitModal count={count} items={items} totalUnits={totalUnits} onConfirm={submitCount} onClose={() => setShowSubmitModal(false)} />}
        {showClosureModal && <ClosureModal count={count} form={closureForm} onChange={(k, v) => setClosureForm(p => ({ ...p, [k]: v }))} onConfirm={flagForClosure} onClose={() => setShowClosureModal(false)} />}
        {showScanModal    && <ScanModal onScan={handleScanConfirm} onClose={() => setShowScanModal(false)} onAddManual={() => { setShowScanModal(false); setShowCustomModal(true); setCustomModalValue(''); setScanTriggeredCustomModal(true); }} />}
        {showCustomModal  && <CustomItemModal location={count?.warehouse?.name} initialValue={customModalValue} onSave={addCustomItem} showQty={scanTriggeredCustomModal} onClose={() => { setShowCustomModal(false); setCustomModalValue(''); setScanTriggeredCustomModal(false); }} />}
        {noteModal        && <NoteModal item={noteModal} onSave={saveNote} onClose={() => setNoteModal(null)} />}
        {/* **The compact tree did not render this at all**, which the desk tree
            did: below 1024 the row menu offered "Edit this line" and pressing it
            opened nothing whatsoever. Found by pressing it in a real browser at
            834, not by reading. */}
        {editModal        && <EditItemModal item={editModal} onSave={saveItemEdit} onClose={() => setEditModal(null)} />}
        {removeModal      && <RemoveModal item={removeModal} onConfirm={removeItem} onClose={() => setRemoveModal(null)} />}
        {dupeModal && (
          <DupeLocationModal
            existingItem={dupeModal.existingItem}
            countedByName={dupeModal.countedByName}
            onAddLocation={loc => {
              const d = dupeModalRef.current;
              if (!d) return;
              if (d.pendingCatalogItem) addCatalogItem(d.pendingCatalogItem, loc);
              else if (d.pendingCustom) addCustomItem(d.pendingCustom, loc);
            }}
            onCancel={() => setDupeModal(null)}
          />
        )}
      </div>
    );
  }
  //  END TABLET/MOBILE LAYOUT 

  /* The desk: the top bar carries the sheet's name, status and cycle (rebrand
  step 4); the live totals and the actions are at the top of the page, and
  Submit is on the floating count bar. */
  return (
    <DeskFrame
      contentStyle={{ display: 'flex', flexDirection: 'column', gap: 0, overflow: 'hidden', paddingTop: 98, paddingBottom: 88 }}
      header={
        <AppHeader
          userName={profile?.full_name}
          roleLabel={profile?.role === 'admin' ? 'Administrator' : profile?.role === 'manager' ? 'Manager' : 'Sales Rep'}
          onSignOut={async () => { await supabase.auth.signOut(); navigate('/login'); }}
          context={
            <>
              <div style={{ fontSize: count?.warehouse?.name?.length > 38 ? 14 : count?.warehouse?.name?.length > 24 ? 15.5 : 17, fontWeight: 800, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                {count?.warehouse?.name}
              </div>
              <div style={{ flexShrink: 0 }}><StatusPill status={count?.status} /></div>
              {/* **A shared location says so on the page bar**, as the approved
                  drawing has it. Two of the company's locations allow several
                  people to count at once and the desk screen said nothing at
                  all about it. */}
              {isWarehouse && <span className="chip-shared">Shared location</span>}
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>{count?.cycle?.name}</div>
            </>
          }
        />
      }
      after={<>
      {/*  Modals  */}
      {showSubmitModal  && <SubmitModal count={count} items={items} totalUnits={totalUnits} onConfirm={submitCount} onClose={() => setShowSubmitModal(false)} />}
      {showClosureModal && <ClosureModal count={count} form={closureForm} onChange={(k, v) => setClosureForm(p => ({ ...p, [k]: v }))} onConfirm={flagForClosure} onClose={() => setShowClosureModal(false)} />}
      {showScanModal    && <ScanModal onScan={handleScanConfirm} onClose={() => setShowScanModal(false)} onAddManual={() => { setShowScanModal(false); setShowCustomModal(true); setCustomModalValue(''); setScanTriggeredCustomModal(true); }} />}
      {showCustomModal  && (
        <CustomItemModal
          location={count?.warehouse?.name}
          initialValue={customModalValue}
          onSave={addCustomItem}
          showQty={scanTriggeredCustomModal}
          onClose={() => { setShowCustomModal(false); setCustomModalValue(''); setScanTriggeredCustomModal(false); }}
        />
      )}
      {dupeModal && (
        <DupeLocationModal
          existingItem={dupeModal.existingItem}
          countedByName={dupeModal.countedByName}
          onAddLocation={loc => {
            const d = dupeModalRef.current;
            if (!d) return;
            if (d.pendingCatalogItem) addCatalogItem(d.pendingCatalogItem, loc);
            else if (d.pendingCustom) addCustomItem(d.pendingCustom, loc);
          }}
          onCancel={() => setDupeModal(null)}
        />
      )}
      {noteModal        && <NoteModal item={noteModal} onSave={saveNote} onClose={() => setNoteModal(null)} />}
      {editModal        && <EditItemModal item={editModal} onSave={saveItemEdit} onClose={() => setEditModal(null)} />}
      {removeModal      && <RemoveModal item={removeModal} onConfirm={removeItem} onClose={() => setRemoveModal(null)} />}
      <CountBar status={count?.status} progress={progress}>
        <button className="btn btn-primary" onClick={() => setShowSubmitModal(true)} disabled={submitting}>
          Submit count
        </button>
      </CountBar>
      </>}
    >
      {/* The sheet's live totals and its actions, drawn at the top of the page
          since rebrand step 4 (the bar no longer carries a page's controls).
          Submit moved to the floating count bar at the bottom (guide 11.9). */}
      <PageBar
        controls={
          <>
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--action-text)' }}>{totalItems}</span>
              <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--action-text)' }}>Lines</span>
            </span>
            <span className="tb-vr" />
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--accent-ink)' }}>{totalUnits}</span>
              <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--accent-ink)' }}>Units</span>
            </span>
            <span className="tb-vr" />
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--success-ink)' }}>{fmtValue}</span>
              <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--success-ink)' }}>Est. Value</span>
            </span>
            {/* **How much is left, on the bar**, which the drawing shows as a
                thin bar and two numbers. It is the sheet's own answer to the
                question a rep opens this screen with. */}
            <span className="tb-vr" />
            <SheetProgress {...progress} />
          </>
        }
        actions={
          <>
            <button className="tb-btn tb-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={14} /> Dashboard</button>
            <button className="tb-btn tb-btn-danger" onClick={() => setShowClosureModal(true)}>Flag Closure</button>
            <span className="tb-vr" />
            <button className="tb-btn" onClick={saveProgress} disabled={saving}>
              {saving ? 'Saving...' : 'Save Progress'}
            </button>
          </>
        }
      />

          {/* **The connection bar, under the page bar and above the work.**
              Group H's approved drawing puts it here rather than over the
              screen, so nothing a person is reading moves out from under them.
              It draws nothing at all when there is a signal and nothing is
              waiting, which is almost always. */}
          <ConnectionBar />

          {/* **The shared location band, from the approved drawing.** It names
              whoever else is in this sheet right now and says what happens to
              the work, which are the two questions a person has the moment they
              realise somebody else is in here. */}
          {isWarehouse && <SharedBand says={sharedSays} live={others.length > 0} />}

          {/* Admin override banner */}
          {adminOverriding && (
            <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--oh-radius-control)', padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 }}>
              <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', background: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ stroke: 'var(--warning-ink)' }} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--warning-ink)', marginBottom: 2 }}>Admin Override - Count In Progress by {ownerName}</div>
                <div style={{ fontSize: 12, color: 'var(--warning-ink)', lineHeight: 1.5 }}>
                  You are editing a count currently claimed by {ownerName}. Any changes you make will affect their count data.
                </div>
              </div>
            </div>
          )}

          {/* **The quick entry bar** (rebrand step 5, guide 12.3): scan or type
              an item on this sheet, then its count. */}
          <QuickEntry ref={quickRef} items={sortedItems} onFind={showFound} onSave={quickSave} />

          {/* Search row: adding an item that is not on the sheet yet */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexShrink: 0 }}>

            {/* Item lookup  single search field */}
            <div style={{ flex: 1, position: 'relative' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}><span className="oh-ground-tag">Add an item</span></div>
              <input
                ref={searchRef}
                value={descSearch}
                onChange={e => setDescSearch(e.target.value.toUpperCase())}
                placeholder="Search the catalog by description, vendor or part #"
                aria-label="Add an item to this sheet"
                style={{ width: '100%', padding: '10px 14px', border: 'none', borderRadius: 'var(--oh-radius-control)', fontSize: 14, fontFamily: 'inherit', background: 'var(--surface-data)', boxShadow: descSearch ? 'inset 0 0 0 2px var(--action)' : 'inset 0 0 0 1px var(--border)', outline: 'none' }}
              />
              {/* Catalog dropdown */}
              {showCatalogDropdown && (
                <div style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', zIndex: 50, background: 'var(--surface-card)', border: '1.5px solid var(--action)', borderRadius: 12, boxShadow: 'var(--depth-lift)', overflow: 'hidden' }}>
                  <div style={{ padding: '7px 14px', background: 'var(--info-bg)', borderBottom: '1px solid var(--info-bg)', fontSize: 11, fontWeight: 700, color: 'var(--info-ink)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between' }}>
                    <span>From Catalog - not in your count</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{searching ? 'Searching...' : catalogResults.length + ' result' + (catalogResults.length !== 1 ? 's' : '')}</span>
                  </div>
                  {catalogResults.map(r => (
                    <div key={r.id} {...pressable(() => addCatalogItem(r))}
                      style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border-soft)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-data)'}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 2 }}>
                          <span className="item-id">{r.item_number}</span>
                          <span className="item-vendor">{r.vendor}</span>
                        </div>
                        <div className="item-desc">{r.description}</div>
                      </div>
                      <button className="add-btn">+ Add</button>
                    </div>
                  ))}
                </div>
              )}
              {showAddCustom && (
                <div {...pressable(() => { setCustomModalValue(descSearch.toUpperCase()); setShowCustomModal(true); })}
                  style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', padding: '10px 14px', background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 'var(--oh-radius-chip)', cursor: 'pointer', fontSize: 13, color: 'var(--accent-ink)', fontWeight: 600, zIndex: 50 }}>
                  + Add "{descSearch}" as custom item (not in catalog)
                </div>
              )}
            </div>

            {/* Item Locations toggle - inline with the search it affects.
                **The whole thing is the switch, not just the little track**,
                which is what the compact screen already did and what rule 13
                asks for: a 20 pixel tall track is a quarter of the 44 pixels a
                finger needs, and pressing the words did nothing at all. */}
            <div {...pressable(toggleItemLocations, { role: 'switch', 'aria-checked': !!locationEnabled })}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 'var(--oh-radius-control)', background: 'var(--surface-glass)', boxShadow: 'inset 0 0 0 1px var(--border)', alignSelf: 'flex-end', cursor: 'pointer', minHeight: 44, boxSizing: 'border-box' }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--action-text)', whiteSpace: 'nowrap' }}>Item Locations</div>
              <div
                style={{ width: 36, height: 20, borderRadius: 'var(--oh-radius-control)', background: locationEnabled ? 'var(--accent)' : 'var(--border-strong)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
              >
                <div style={{ position: 'absolute', top: 2, left: locationEnabled ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: 'var(--surface-card)', transition: 'left 0.2s' }} />
              </div>
            </div>
          </div>

          {/* Item table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* **The same list the phone and the tablet draw.** This was a
                separate HTML table of its own, and rule 15 asks for a grid
                anyway. */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 4px' }}>
              <LineList {...lineListProps} />
            </div>


          </div>
    </DeskFrame>
  );
}
